/* The free review tools (sections/st-review-tool.liquid): the Google review link finder (data-mode
   "link") and the Google review QR code maker (data-mode "qr"). Ported from the Rise theme
   (assets/review-link-generator.js, 2026-09-27) with the same parser, and redesigned.

   - No Google API. It accepts what a business owner can already copy: a g.page review link, a link
     with placeid= (or place_id=) in it, or a bare Place ID, and returns the review form link in one
     standard shape. A Maps share link cannot become a review link without the Places API, so it is
     recognised and answered with the manual steps instead.
   - Safety: the link is rebuilt only from parts that matched [A-Za-z0-9_-], so nothing typed reaches
     the page or the link as markup or as another scheme. Messages and the link are set with
     textContent; the QR code is parsed as SVG by DOMParser, never written with innerHTML.
   - One status line under the button (role=status) says what the submit did; a second one in the
     result reports copy and download. Both stay in the page while empty, so the first message is
     announced. The result stays hidden until there is one.
   - QR mode draws the link with assets/st-qrcode-generator.js (qrcode-generator 2.0.4, MIT), which
     only that mode loads. SVG and PNG downloads; the PNG is the same SVG drawn at its own pixel size
     on a canvas (an SVG with no width and height draws at 0 in Firefox, so the PNG copy has them).
     If the browser cannot load or export the image, a message says so and points to the SVG (the
     Rise version failed silently there: review finding, 2026-10-12).
   - The link finder's "Make a QR code" link carries the link to the QR page in the URL fragment
     (#link=...), which never reaches the server; the QR page reads it back through the same parser.
   - A link to a question on the page (the input's hint) opens that question before the page jumps. */
if (!customElements.get('st-review-tool')) {
  class StReviewTool extends HTMLElement {
    static fromPlaceId(id) {
      return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(id)}`;
    }

    static parse(raw) {
      const text = String(raw ?? '').trim();
      if (!text) return { error: 'empty' };

      // A bare Place ID: no dots, no slashes, the character set Google uses.
      if (!/[./\s]/.test(text) && /^[A-Za-z0-9_-]{20,}$/.test(text)) {
        return { link: StReviewTool.fromPlaceId(text) };
      }

      let url;
      try {
        url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
      } catch (error) {
        return { error: 'invalid' };
      }
      const host = url.hostname.replace(/^www\./, '').toLowerCase();

      const placeId = url.searchParams.get('placeid') || url.searchParams.get('place_id');
      if (placeId && /^[A-Za-z0-9_-]{20,}$/.test(placeId)) {
        return { link: StReviewTool.fromPlaceId(placeId) };
      }

      if (host === 'g.page') {
        const match = url.pathname.match(/^\/r\/([A-Za-z0-9_-]+)/);
        if (match) return { link: `https://g.page/r/${match[1]}/review` };
      }

      if (host === 'maps.app.goo.gl' || host === 'goo.gl' || /(^|\.)google\.[a-z.]+$/.test(host)) {
        return { error: 'share' };
      }

      return { error: 'invalid' };
    }

    // The link carried from the link finder, as typed into the fragment (#link=...); it still goes
    // through parse() before anything uses it.
    static carried(hash) {
      return new URLSearchParams(String(hash ?? '').replace(/^#/, '')).get('link') || '';
    }

    connectedCallback() {
      this.mode = this.dataset.mode === 'qr' ? 'qr' : 'link';
      this.form = this.querySelector('[data-st-form]');
      this.input = this.querySelector('[data-st-input]');
      this.msg = this.querySelector('[data-st-msg]');
      this.result = this.querySelector('[data-st-result]');
      this.note = this.querySelector('[data-st-note]');
      this.qr = this.querySelector('[data-st-qr]');
      this.carry = this.querySelector('[data-st-carry]');
      if (!this.form || !this.input || !this.result) return;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#generate();
      });
      this.querySelector('[data-st-copy]')?.addEventListener('click', () => this.#copy());
      this.querySelector('[data-st-svg]')?.addEventListener('click', () => this.#downloadSvg());
      this.querySelector('[data-st-png]')?.addEventListener('click', () => this.#downloadPng());
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-st-open]');
        const question = link && document.getElementById(link.hash.slice(1));
        if (!(question instanceof HTMLDetailsElement)) return;
        question.open = true;
        setTimeout(() => question.querySelector('summary')?.focus(), 0);
      });

      const carried = StReviewTool.carried(window.location.hash);
      if (carried) {
        this.input.value = carried;
        this.#generate();
      }
    }

    #say(el, text, tone) {
      if (!el) return;
      el.textContent = text || '';
      if (tone && text) el.dataset.tone = tone;
      else delete el.dataset.tone;
    }

    #generate() {
      const { link, error } = StReviewTool.parse(this.input.value);
      this.#say(this.note, '');
      if (error) {
        this.result.hidden = true;
        this.input.setAttribute('aria-invalid', 'true');
        this.#say(this.msg, this.dataset[`error${error.charAt(0).toUpperCase()}${error.slice(1)}`], 'error');
        return;
      }
      this.input.removeAttribute('aria-invalid');
      if (this.mode === 'qr' && !this.#drawQr(link)) {
        this.result.hidden = true;
        this.#say(this.msg, this.dataset.errorQr, 'error');
        return;
      }
      this.link = link;
      this.querySelectorAll('[data-st-out]').forEach((el) => {
        el.textContent = link;
      });
      this.querySelectorAll('[data-st-test]').forEach((a) => {
        a.href = link;
      });
      if (this.carry) {
        const base = this.carry.getAttribute('href').split('#')[0];
        this.carry.href = `${base}#${new URLSearchParams({ link })}`;
      }
      this.result.hidden = false;
      this.#say(this.msg, this.dataset.ready, 'ok');
    }

    #drawQr(link) {
      if (!this.qr || typeof window.qrcode !== 'function') return false;
      try {
        const code = window.qrcode(0, 'M');
        code.addData(link);
        code.make();
        // On screen and in the SVG file: scalable, four modules of quiet zone. For the PNG: whole
        // pixels per module, about 1200px square, with its size written on the svg element.
        this.svg = code.createSvgTag({ cellSize: 8, margin: 32, scalable: true });
        const cell = Math.max(4, Math.floor(1200 / (code.getModuleCount() + 8)));
        this.pngSvg = code.createSvgTag({ cellSize: cell, margin: cell * 4 });
        this.pngSize = (code.getModuleCount() + 8) * cell;
        const node = new DOMParser().parseFromString(this.svg, 'image/svg+xml').documentElement;
        if (node.localName !== 'svg') return false;
        node.setAttribute('aria-hidden', 'true');
        node.setAttribute('focusable', 'false');
        this.qr.replaceChildren(document.importNode(node, true));
        return true;
      } catch (error) {
        return false;
      }
    }

    #save(blob, filename) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    #downloadSvg() {
      if (!this.svg) return;
      this.#say(this.note, '');
      this.#save(new Blob([this.svg], { type: 'image/svg+xml' }), `${this.dataset.file || 'qr-code'}.svg`);
    }

    #downloadPng() {
      if (!this.pngSvg) return;
      this.#say(this.note, '');
      const fail = () => this.#say(this.note, this.dataset.errorPng, 'error');
      const size = this.pngSize;
      const url = URL.createObjectURL(new Blob([this.pngSvg], { type: 'image/svg+xml' }));
      const image = new Image();
      image.onerror = () => {
        URL.revokeObjectURL(url);
        fail();
      };
      image.onload = () => {
        URL.revokeObjectURL(url);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const context = canvas.getContext('2d');
          if (!context) return fail();
          context.imageSmoothingEnabled = false;
          context.drawImage(image, 0, 0, size, size);
          canvas.toBlob((blob) => (blob ? this.#save(blob, `${this.dataset.file || 'qr-code'}.png`) : fail()), 'image/png');
        } catch (error) {
          fail();
        }
      };
      image.src = url;
    }

    async #copy() {
      if (!this.link) return;
      try {
        await navigator.clipboard.writeText(this.link);
        this.#say(this.note, this.dataset.copied, 'ok');
      } catch (error) {
        const out = this.querySelector('[data-st-out]');
        const selection = window.getSelection();
        if (out && selection) {
          const range = document.createRange();
          range.selectNodeContents(out);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        this.#say(this.note, this.dataset.copyFallback);
      }
    }
  }
  customElements.define('st-review-tool', StReviewTool);
}
